import * as React from 'react';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { AndroidDependenciesDependencyReport } from '@/types/androidDependencies';
import {
    dependencyGraphChain,
    formatResolutionLabel,
    formatVersionStatusLabel,
    isCatalogOnly,
    isGradlePlugin,
    isIndirect,
    versionStatusTagColor,
} from '@/components/dependencies/androidDependenciesUtils';

const AndroidDependencyDetailContent: React.FC<{ row: AndroidDependenciesDependencyReport }> = ({ row }) => {
    const chain = dependencyGraphChain(row.dependencyGraph);

    return (
        <Space direction="vertical" size={8} style={{ width: 360, maxWidth: '90vw' }}>
            <Space direction="vertical" size={0}>
                <Text strong>{row.artifact}</Text>
                <Text type="secondary" style={{ fontSize: 12 }} copyable={{ text: row.coordinate }}>
                    {row.coordinate}
                </Text>
            </Space>
            <Descriptions size="small" column={1} colon={false} labelStyle={{ width: 130 }}>
                <Descriptions.Item label="Status">
                    <Tag color={versionStatusTagColor(row.versionStatus)}>
                        {formatVersionStatusLabel(row.versionStatus)}
                    </Tag>
                </Descriptions.Item>
                {(row.declarations ?? []).length > 0 && (
                    <Descriptions.Item label="Declared in">
                        <Space direction="vertical" size={6}>
                            {(row.declarations ?? []).map((site) => (
                                <Space
                                    key={site.line ? `${site.file}:${site.line}` : site.file}
                                    direction="vertical"
                                    size={2}
                                >
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {site.line ? `${site.file}:${site.line}` : site.file}
                                    </Text>
                                    {site.declaration && (
                                        <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                            {site.declaration}
                                        </Text>
                                    )}
                                </Space>
                            ))}
                        </Space>
                    </Descriptions.Item>
                )}
                {row.checkError && (
                    <Descriptions.Item label="Check error">
                        <Text type="danger" style={{ fontSize: 12 }}>
                            {row.checkError}
                        </Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Resolution">{formatResolutionLabel(row.status)}</Descriptions.Item>
                <Descriptions.Item label="Source">
                    {isGradlePlugin(row) ?
                        <Tag color="blue">Plugin</Tag>
                    : isCatalogOnly(row) ?
                        <Tag color="purple">Catalog</Tag>
                    : isIndirect(row) ?
                        <Tag>Transitive</Tag>
                    :   <Tag color="magenta">Direct</Tag>}
                </Descriptions.Item>
                {chain && (
                    <Descriptions.Item label="Pulled in by">
                        <Text style={{ fontSize: 12 }}>{chain}</Text>
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Group">{row.group || '—'}</Descriptions.Item>
                <Descriptions.Item label="Artifact">{row.artifact || '—'}</Descriptions.Item>
                <Descriptions.Item label="Current">{row.currentVersion || '—'}</Descriptions.Item>
                <Descriptions.Item label="Latest">{row.latestVersion || '—'}</Descriptions.Item>
                {row.latestPreReleaseVersion && (
                    <Descriptions.Item label="Latest pre-release">{row.latestPreReleaseVersion}</Descriptions.Item>
                )}
            </Descriptions>
        </Space>
    );
};

export default AndroidDependencyDetailContent;
